import { AfterViewInit, Component, Inject, OnDestroy } from '@angular/core';
import { MatOption } from '@angular/material/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsersProvider } from '../../../users/providers/users-provider';
import { Title } from '@angular/platform-browser';
import { ConfigProvider } from "../../../config-provider.service";
import { UtilsProvider } from "../../../common/providers/utils-provider.service";
import { AthleteRow, AthletesProvider } from '../../providers/athletes-provider';
import { AddressesProvider, AddressRow } from '../../../addresses/providers/addresses-provider';
import { FilesProvider } from '../../../files/providers/files-provider';
import { Observable, Subscription } from 'rxjs';
import {
  MAT_DIALOG_DATA, MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Lang } from "../../../common/models/lang";
import { Gender } from "../../../common/models/gender";
import { ContractType } from "../../../common/models/contract-type";
import { Team } from "../../../common/models/team";
import { User } from "../../../common/models/user";
import { Organization } from "../../../common/models/organization";
import { CurrentUser } from "../../../common/providers/current-user";
import { LangsProvider } from "../../../common/providers/langs-provider";
import { Athlete } from "../../../common/models/athlete";
import { Address } from "../../../common/models/address";
import { AddressType } from '../../../common/models/address-type';
import { NgForOf, NgIf } from "@angular/common";
import { MatButton, MatIconButton } from "@angular/material/button";
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from "@angular/material/form-field";
import { InputImageComponent } from "../../../common/components/input-image/input-image";
import { MatInput } from "@angular/material/input";
import { MatProgressBar } from "@angular/material/progress-bar";
import { MatRadioButton, MatRadioGroup } from "@angular/material/radio";
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from "@angular/material/datepicker";
import { MatSelect } from "@angular/material/select";
import { MatTooltip } from "@angular/material/tooltip";
import { DateClosestPipe } from "../../../common/pipes/date-closest";
import { MatIcon } from "@angular/material/icon";
import { CdkTextareaAutosize } from "@angular/cdk/text-field";

@Component({
  selector: 'athletes-form',
  providers: [],
  styleUrls: ['./form.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatOption,
    NgIf,
    NgForOf,
    MatDialogClose,
    MatButton,
    MatFormField,
    InputImageComponent,
    MatSuffix,
    MatDialogTitle,
    MatInput,
    MatProgressBar,
    MatIconButton,
    FormsModule,
    MatRadioButton,
    MatDatepickerInput,
    MatSelect,
    MatTooltip,
    TranslateModule,
    DateClosestPipe,
    MatIcon,
    MatHint,
    MatDialogContent,
    MatDialogActions,
    MatError,
    MatRadioGroup,
    MatLabel,
    MatDatepicker,
    MatDatepickerToggle,
    CdkTextareaAutosize,
  ],
  templateUrl: './form.html'
})
export class AthletesFormComponent implements AfterViewInit, OnDestroy {
    public readonly NAME_MAX_LENGTH: number = 200;
    public readonly DESCRIPTION_MAX_LENGTH: number = 1000;
    public readonly GENDER_MALE: number = Gender.ID_MALE;
    public readonly GENDER_FEMALE: number = Gender.ID_FEMALE;

    public readonly CHECK_EMAIL_THRESHOLD: number = 750;
    public isLoading = false;
    public formTitle = '';
    public confirmButtonTitle = 'Save';
    public nextButtonTitle = 'Next';
    public previousButtonTitle = 'Previous';

    public availableGenders: Gender[] = [];
    public availableContractTypes: ContractType[] = [];
    public availableTeams: Team[] = [];
    public availableCoaches: User[] = [];

    public organisation: Organization | null = null;
    public athleteId = 0;
    public userId = 0;
    public defaultAddressId = 0;

    public isAdmin = 0;
    public langId: number = Lang.ID_DEFAULT;
    public contractTypeSelected: number = ContractType.ID_DEFAULT;

    public avatarImage: any = { current: null, new: null };

    // affiche par défaut le premier panneau
    public displayFirstStep = true;
    public genderTouched = false;

    public genderFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public firstnameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public lastnameFormControl: FormControl = new FormControl('', [
        Validators.required
    ]);
    public birthdayFormControl: FormControl = new FormControl('');
    public birthdayMinDate: Date = new Date('1900-01-01 00:00:00');
    public birthdayMaxDate: Date = new Date();
    public emailUnavailableError = false;
    public emailFormControl: FormControl = new FormControl('', [
        Validators.required,
        Validators.email,
        (control: any) => this.emailUnavailableError ? { unavailable: { valid: false } } : null
    ]);
    public billingEmailFormControl: FormControl = new FormControl('', [Validators.email]);
    public postcodeFormControl: FormControl = new FormControl('', [
        Validators.pattern('^[0-9]*$')
    ]);
    public cityFormControl: FormControl = new FormControl('');
    public streetNameFormControl: FormControl = new FormControl('');
    public buildingNumberFormControl: FormControl = new FormControl('');
    public addressAdditionFormControl: FormControl = new FormControl('');
    public countryFormControl: FormControl = new FormControl('CH', [
        Validators.pattern('^[A-Z]{2}$')
    ]);
    public sportsPracticedFormControl: FormControl = new FormControl('');
    public clubFormControl: FormControl = new FormControl('');
    public athleteDescriptionFormControl: FormControl = new FormControl('');
    public contractTypeFormControl: FormControl = new FormControl(1, [Validators.required]);
    public phoneNumberFormControl: FormControl = new FormControl('');
    public externalCoachNameFormControl: FormControl = new FormControl('');
    public sizeFormControl: FormControl = new FormControl(0, [Validators.min(0)]);
    public weightFormControl: FormControl = new FormControl(0, [Validators.min(0)]);

    private _checkEmailAvailabilityRequestTimestamp = 0;
    private _originalEmail: string | null = null;
    private subscriptions: Subscription[] = [];
    constructor(
        public conf: ConfigProvider,
        public currentUser: CurrentUser,
        public utils: UtilsProvider,
        public dialog: MatDialogRef<AthletesFormComponent>,
        public usersProvider: UsersProvider,
        private _toaster: MatSnackBar,
        private _athletesProvider: AthletesProvider,
        private _translater: TranslateService,
        @Inject(MAT_DIALOG_DATA) private _data : any
    ) {
        this.availableGenders = this._data.availableGenders;
        this.availableContractTypes = this._data.availableContractTypes;
        this.availableCoaches = this._data.availableCoaches;
        this.availableTeams = this._data.availableTeams;
        this.organisation = _data.organization;

        if (this._data.athleteRow) {

            this.athleteId = this._data.athleteRow.athlete.id;
            this.isAdmin = this._data.athleteRow.user.isAdmin;
            this.userId = this._data.athleteRow.user.id;
            this.langId = this._data.athleteRow.user.langId;
            this.genderFormControl.setValue(this._data.athleteRow.athlete.genderId);

            this.firstnameFormControl.setValue(this._data.athleteRow.user.firstName);
            this.lastnameFormControl.setValue(this._data.athleteRow.user.lastName);
            this.emailFormControl.setValue(this._data.athleteRow.user.email);
            this.billingEmailFormControl.setValue(this._data.athleteRow.athlete.billingEmail);
            this._originalEmail = this._data.athleteRow.user.email;
            if (this._data.athleteRow.athlete.birthday) {
                this.birthdayFormControl.setValue(this._data.athleteRow.athlete.birthday);
            }
            if (this._data.athleteRow.addresses.length) {
                this.defaultAddressId = this._data.athleteRow.addresses[0].id;
                this.postcodeFormControl.setValue(this._data.athleteRow.addresses[0].postcode);
                this.cityFormControl.setValue(this._data.athleteRow.addresses[0].city);
                this.streetNameFormControl.setValue(this._data.athleteRow.addresses[0].streetName);
                this.buildingNumberFormControl.setValue(this._data.athleteRow.addresses[0].buildingNumber);
                this.addressAdditionFormControl.setValue(this._data.athleteRow.addresses[0].addressAddition);
                if (this._data.athleteRow.addresses[0].country) {
                    this.countryFormControl.setValue(this._data.athleteRow.addresses[0].country);
                }
            }
            this.phoneNumberFormControl.setValue(this._data.athleteRow.user.phoneNumber);
            this.externalCoachNameFormControl.setValue(this._data.athleteRow.athlete.externalCoachName);
            this.sizeFormControl.setValue(this._data.athleteRow.athlete.size);
            this.weightFormControl.setValue(this._data.athleteRow.athlete.weight);
            this.avatarImage.current = _data.athleteRow.user.avatar;

            this.sportsPracticedFormControl.setValue(this._data.athleteRow.athlete.sportsPracticed);
            this.clubFormControl.setValue(this._data.athleteRow.athlete.clubName);
            this.athleteDescriptionFormControl.setValue(this._data.athleteRow.athlete.description);

            this.contractTypeSelected = this._data.athleteRow.contractType.id;
            this.contractTypeFormControl.setValue(this.contractTypeSelected);
        }
        this.subscriptions.push(
            this.genderFormControl.valueChanges.subscribe(() => { this.genderTouched = true; })
        );

        this.formTitle = this._data.formTitle;
        this.confirmButtonTitle = this._data.confirmButtonTitle;
    }

    public ngAfterViewInit() {
    }

    public ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    public onNextClicked(): void {
        if (this.isFirstStepValid()) {
            this.displayFirstStep = false;
        } else {
            this.touchFirstStepFields();
            this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
        }
    }

    public onPreviousClicked(): void {
        this.displayFirstStep = true;
    }

    public onSaveClicked(): void {
        if (this.isSecondStepValid()) {
            this.isLoading = true;
            this.save().subscribe({
                next: (result: AthleteRow) => {
                    this._toaster.open(this._translater.instant('Athlete_saved'), '', { duration: 5000 });
                    this.isLoading = false;
                    this.dialog.close(result);
                },
                error: (error: any) => this.onError(error)
            })
        } else {
            this.touchSecondStepFields();
            this._toaster.open(this._translater.instant('Correct_form_errors'), '', { duration: 5000 });
        }
    }

    public canSave(): boolean {

        return !this.isLoading
            && !this.genderFormControl.invalid
            && !this.firstnameFormControl.invalid
            && !this.lastnameFormControl.invalid
            && !this.emailFormControl.invalid
            && !this.billingEmailFormControl.invalid
            && !this.birthdayFormControl.invalid
            && !this.postcodeFormControl.invalid
            && !this.cityFormControl.invalid
            && !this.streetNameFormControl.invalid
            && !this.sizeFormControl.invalid
            && !this.weightFormControl.invalid;
    }

    public onEmailChanged(): void {
        this.emailUnavailableError = false;
        this.emailFormControl.markAsTouched();
        this.emailFormControl.updateValueAndValidity();
        if (!this.emailFormControl.hasError('email') && this.emailFormControl.value != this._originalEmail) {
            // on check la dispo que si l'email est valide et différente de l'originale ...
            this.checkEmailAvailability();
        }
    }

    private isFirstStepValid(): boolean {
        return !this.genderFormControl.invalid
            && !this.firstnameFormControl.invalid
            && !this.lastnameFormControl.invalid
            && !this.birthdayFormControl.invalid
            && !this.postcodeFormControl.invalid
            && !this.cityFormControl.invalid
            && !this.streetNameFormControl.invalid
            && !this.phoneNumberFormControl.invalid
            && !this.emailFormControl.invalid
            && !this.billingEmailFormControl.invalid;
    }

    private isSecondStepValid(): boolean {
        return !this.sportsPracticedFormControl.invalid
            && !this.clubFormControl.invalid
            && !this.externalCoachNameFormControl.invalid
            && !this.contractTypeFormControl.invalid
            && !this.athleteDescriptionFormControl.invalid
            && !this.sizeFormControl.invalid
            && !this.weightFormControl.invalid;
    }

    private touchFirstStepFields(): void {
        this.genderTouched = true;
        this.firstnameFormControl.markAsTouched();
        this.lastnameFormControl.markAsTouched();
        this.birthdayFormControl.markAsTouched();
        this.postcodeFormControl.markAsTouched();
        this.cityFormControl.markAsTouched();
        this.streetNameFormControl.markAsTouched();
        this.phoneNumberFormControl.markAsTouched();
        this.emailFormControl.markAsTouched();
        this.billingEmailFormControl.markAsTouched();
    }

    private touchSecondStepFields(): void {
        this.sizeFormControl.markAsTouched();
        this.weightFormControl.markAsTouched();
        this.sportsPracticedFormControl.markAsTouched();
        this.clubFormControl.markAsTouched();
        this.externalCoachNameFormControl.markAsTouched();
        this.contractTypeFormControl.markAsTouched();
        this.athleteDescriptionFormControl.markAsTouched();
    }

    private checkEmailAvailability(): void {
        this._checkEmailAvailabilityRequestTimestamp = (new Date()).getTime();
        setTimeout(() => {
            const now: number = (new Date()).getTime();
            if (now - this._checkEmailAvailabilityRequestTimestamp >= this.CHECK_EMAIL_THRESHOLD) {
                this.usersProvider.checkEmailAvailability({email: this.emailFormControl.value, deep: false}).subscribe({
                    next: (isAvailable: boolean) => {
                        this.emailUnavailableError = !isAvailable;
                        this.emailFormControl.updateValueAndValidity();
                    },
                    error: (error: any) => this.onError(error)
                });
            }
        }, this.CHECK_EMAIL_THRESHOLD);
    }

    private save(): Observable<AthleteRow> {
          const athleteRow: AthleteRow = {
              user: {} as User,
              athlete: {} as Athlete,
              gender: {} as Gender,
              contractType: {} as ContractType,
              addresses: [],
              coaches: [],
              coachNames: '',
              teams: [],
              teamNames: '',
              rights: undefined
          };

          athleteRow.athlete.id = this.athleteId;
          athleteRow.user.id = this.userId;

          // athlete
          // des fois le composant contient un objet Date (quand il est untouched) et des fois un objet Moment (quand on a tappé dedans ...)
          let birthdate: string | null = null;
          if (this.birthdayFormControl.value) {
              birthdate = this.birthdayFormControl.value.format ?
                  this.birthdayFormControl.value.format('YYYY-MM-DD') :
                  this.utils.DateToYmd(new Date(this.birthdayFormControl.value));
          }
          athleteRow.athlete.genderId = this.genderFormControl.value;
          athleteRow.athlete.contractTypeId = this.contractTypeFormControl.value;
          athleteRow.athlete.userId = this.userId;
          athleteRow.athlete.birthday = birthdate ?? undefined;
          athleteRow.athlete.sportsPracticed = this.sportsPracticedFormControl.value;
          athleteRow.athlete.externalCoachName = this.externalCoachNameFormControl.value;
          athleteRow.athlete.clubName = this.clubFormControl.value;
          athleteRow.athlete.description = this.athleteDescriptionFormControl.value;
          athleteRow.athlete.size = this.sizeFormControl.value;
          athleteRow.athlete.weight = this.weightFormControl.value;
          athleteRow.athlete.billingEmail = this.billingEmailFormControl.value;

          // user
          athleteRow.user.firstName = this.firstnameFormControl.value;
          athleteRow.user.lastName = this.lastnameFormControl.value;
          athleteRow.user.email = this.emailFormControl.value;
          athleteRow.user.langId = this.langId;
          athleteRow.user.isAdmin = this.isAdmin;
          athleteRow.user.intro = this.athleteDescriptionFormControl.value;
          athleteRow.user.phoneNumber = this.phoneNumberFormControl.value;
          if (this.avatarImage.new) {
              athleteRow.user.avatar = this.avatarImage.new;
          } else {
              athleteRow.user.avatar = this.avatarImage.current;
          }

          athleteRow.contractType = this.availableContractTypes.find((contractType: ContractType) => contractType.id === this.contractTypeFormControl.value)!;

          // adresse par défaut
          const defaultAddress: Address  =new Address({
              id: this.defaultAddressId,
              athleteId: this.athleteId,
              addressTypeId: AddressType.ID_DEFAULT,
              streetName: this.streetNameFormControl.value,
              buildingNumber: this.buildingNumberFormControl.value,
              addressAddition: this.addressAdditionFormControl.value,
              postcode: this.postcodeFormControl.value,
              city: this.cityFormControl.value,
              country: this.countryFormControl.value
          });
          athleteRow.addresses.push(defaultAddress);

          return this._athletesProvider.saveAthlete(this.organisation?.id!, athleteRow)
    }

    private onError(error: any): void {
        this.isLoading = false;

        if (error.status === 422) {
            if (error.error.errors && error.error.errors.email) {
                // c'est qu'on a essayé de faire valider un email test@test par exemple
                // (c'est validé par le validator angular mais pas laravel...)
                return;
            }
        }

        this._toaster.open(this._translater.instant('Error_unknown'), '', { duration: 5000 });
    }

}
